<template>
  <div class="flex flex-col h-full">
    <yb-drop-down-search
      data-test-id="query-page-copilot-table-selector"
      search-label="Search Tables"
      :options="copilotTables"
      item-id="key"
      item-label="label"
      list-classes="overflow-hidden"
      :close-on-click="false"
      :links="copilotLinks"
      aria-label="Select Tables"
      @link-click="handleLinkClick"
    >
      <yb-drop-down-button-label>
        <template #label>
          <div class="flex items-center space-x-1">
            <yb-icon icon="table" class="h-4 w-4" />
            <div class="font-semibold">
              Tables
            </div>
            <div class="truncate copilot-tables-label">
              {{ copilotSelectedTables.length }} table(s) selected
            </div>
          </div>
        </template>
      </yb-drop-down-button-label>
      <template #item="{ option: t }">
        <yb-drop-down-item
          :key="t.key"
          :data-test-id="`query-page-copilot-table-selector-option-${t.key}`"
        >
          <div
            class="flex items-center w-full cursor-pointer"
            @click.stop="toggleTableSelection(t)"
          >
            <yb-icon
              :icon="t.icon"
              class="h-4 w-4 -ml-4 mr-2 cursor-pointer"
              @click.stop="toggleTableSelection(t)"
            />
            <input
              type="checkbox"
              :checked="isTableSelected(t)"
              class="mr-2"
              @click.stop="toggleTableSelection(t)"
            >
            <span class="cursor-pointer">
              {{ t.schemaName }}.{{ t.tableName }}
            </span>
          </div>
        </yb-drop-down-item>
      </template>
    </yb-drop-down-search>
    <hr>

    <div class="mt-4">
      <span class="font-base text-black dark:text-white">Enter your Question</span>
    </div>
    <div class="mt-4">
      <textarea
        v-model="copilotQuestion"
        class="w-[calc(100%-4px)] border resize-none rounded-md border-gray-300 text-yb-gray-dark dark:bg-yb-gray-mud dark:text-yb-gray-faint"
        rows="4"
        data-test-id="query-page-copilot-question"
      />
    </div>
    <div class="mt-2 flex">
      <yb-button
        :disabled="copilotLoading"
        base-button-class="py-1.5"
        button-classes="yb-button-primary rounded-md text-sm mr-4"
        icon-classes="yb-button-icon-md text-white fill-current"
        data-test-id="query-page-copilot-gen-sql-button"
        @click="genSQL"
      >
        <span v-if="!copilotLoading">Generate SQL</span>
        <span v-else>Generating SQL...</span>
      </yb-button>

      <yb-button
        :disabled="!copilotGeneratedPrompt"
        base-button-class="py-1.5"
        button-classes="yb-button-primary rounded-md text-sm"
        icon-classes="yb-button-icon-md text-white fill-current"
        data-test-id="query-page-copilot-generated-prompt-button"
        @click="showGeneratedPrompt"
      >
        <span>View Generated Prompt</span>
      </yb-button>
    </div>

    <div class="mt-6">
      <span class="font-base text-black dark:text-white">Edit your Prompt</span>
    </div>
    <div class="mt-4 flex-grow">
      <div class="h-full border rounded-md overflow-hidden">
        <yb-text-editor
          v-model="copilotPrompt"
          class="w-full h-full border border-gray-300 text-yb-gray-dark dark:bg-yb-gray-mud dark:text-yb-gray-faint"
          language="text"
          :options="editorOptions"
          data-test-id="query-page-copilot-prompt"
          @editor-did-mount="editorDidMount"
        />
      </div>
    </div>
    <div class="mt-4">
      <yb-button
        base-button-class="py-1.5"
        button-classes="yb-button-primary rounded-md text-sm"
        icon-classes="yb-button-icon-md text-white fill-current"
        data-test-id="query-page-copilot-prompt-reset-button"
        @click="resetEditor"
      >
        <span>Reset to Default</span>
      </yb-button>
    </div>
  </div>
  <copilot-generated-prompt-dialog v-if="showPrompt" :show="showPrompt" @close="showPrompt = false" />
</template>

<script>
import CopilotGeneratedPromptDialog from './CopilotGeneratedPromptDialog.vue'
import Table from '@/models/Table'
import { databaseObjectService } from '@/services'
import { defaultPrompt } from '@/app/query/CopilotPrompt'

export default {
  components: {
    CopilotGeneratedPromptDialog
  },
  inject: ['editor', 'content'],
  emits: ["close"],
  data() {
    return {
      copilotLoading: false,
      copilotLinks: [
        { label: 'All', id: 'all' },
        { label: 'None', id: 'none' }
      ],
      editorOptions: {
        minimap: {
          enabled: false
        },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        lineNumbers: 'off',
        fontSize: 13
      },
      showPrompt: false
    }
  },
  computed: {
    copilotPrompt: {
      get() {
        return this.editor.settings.copilotPrompt || defaultPrompt
      },
      set(value) {
        this.editor.settings.copilotPrompt = value
      }
    },
    copilotQuestion: {
      get() {
        return this.editor.settings.copilotQuestion || ''
      },
      set(value) {
        this.editor.settings.copilotQuestion = value
      }
    },
    copilotGeneratedPrompt: {
      get() {
        return this.editor.settings.copilotGeneratedPrompt || ''
      },
      set(value) {
        this.editor.settings.copilotGeneratedPrompt = value
      }
    },
    copilotSelectedTables: {
      get() {
        return this.editor.settings.copilotSelectedTables || []
      },
      set(value) {
        this.editor.settings.copilotSelectedTables = value
      }
    },
    copilotTables() {
      const selectedSchemas = this.editor.settings.searchPath || []
      return Table.query()
        .where('instance_id', this.editor.settings.instance)
        .where('database_id', this.editor.settings.database_id)
        .get()
        .filter(table => selectedSchemas.includes(table.schema_name))
        .map(table => ({
          schemaName: table.schema_name,
          tableName: table.name,
          key: `${table.schema_name}.${table.name}`,
          label: `${table.schema_name}.${table.name}`,
          icon: 'table'
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }
  },
  methods: {
    showGeneratedPrompt() {
      this.showPrompt = true
    },
    closeDialog() {
      this.showPrompt = false
    },
    editorDidMount() {
      if (!this.copilotPrompt) {
        this.copilotPrompt = defaultPrompt
      }
    },
    resetEditor() {
      this.copilotPrompt = defaultPrompt
      this.$nextTick(() => {
        this.editorOptions = { ...this.editorOptions }
      })
    },
    isTableSelected(table) {
      return this.copilotSelectedTables.some(
        t => t.schemaName === table.schemaName && t.tableName === table.tableName
      )
    },
    toggleTableSelection(table) {
      const updatedTables = this.isTableSelected(table)
        ? this.copilotSelectedTables.filter(
          t => t.schemaName !== table.schemaName || t.tableName !== table.tableName
        )
        : [...this.copilotSelectedTables, table]
      this.copilotSelectedTables = updatedTables
    },
    handleLinkClick(link) {
      this.copilotSelectedTables =
        link.id === 'none' ? [] : [...this.copilotTables]
    },
    cosineSimilarity(a, b) {
      // Returns values between -1 and 1 representing the angle between two embedding vectors.
      // A value of 1 implies the two vectors are identical; a value of 0 implies
      // the vectors are orthogonal; and a value of -1 indicates the vectors are completely opposite.
      // In this context, the vectors represent the prompt and a table name, and the idea
      // is to find the tables that most closely match the prompt
      const dotProduct = a.reduce((sum, ai, i) => sum + ai * b[i], 0)
      const magnitudeA = Math.sqrt(a.reduce((sum, ai) => sum + ai * ai, 0))
      const magnitudeB = Math.sqrt(b.reduce((sum, bi) => sum + bi * bi, 0))
      return dotProduct / (magnitudeA * magnitudeB)
    },
    async getRelevantTables(query, schemaDetails, embeddingModelName, maxRelevantTables) {
      const baseURL = this.$store.get('settings/editor@baseURL')
      const apiKey = this.$store.get('settings/editor@apiKey')

      const response = await fetch('/copilot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          targetUrl: `${baseURL}/v1/embeddings`,
          apiKey,
          model: embeddingModelName,
          input: query,
          encoding_format: 'float'
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = 'Unknown error occurred'
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.details?.error?.message || errorData.message || errorMessage
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError)
          errorMessage = errorText
        }
        console.error('Failed to fetch embeddings:', errorMessage)
        throw new Error(`Failed to fetch embeddings: ${errorMessage}`)
      }

      const data = await response.json()
      const queryEmbedding = data.data[0].embedding

      const tableSimilarities = await Promise.all(
        schemaDetails.map(async (row) => {
          const fqTable = `${row.schemaName}.${row.tableName}`
          const response = await fetch('/copilot', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              targetUrl: `${baseURL}/v1/embeddings`,
              apiKey,
              model: embeddingModelName,
              input: fqTable,
              encoding_format: 'float'
            })
          })

          if (!response.ok) {
            const errorText = await response.text()
            const errorData = JSON.parse(errorText)
            console.error('Failed to fetch embedding for table:', fqTable, errorText)
            throw new Error(`Failed to fetch embedding for table ${fqTable}: ${errorData.message}`)
          }

          if (!response.ok) {
            const errorText = await response.text()
            let errorMessage = 'Unknown error occurred'
            try {
              const errorData = JSON.parse(errorText)
              errorMessage = errorData.details?.error?.message || errorData.message || errorMessage
            } catch (parseError) {
              console.error('Failed to parse error response:', parseError)
              errorMessage = errorText
            }
            console.error('Failed to fetch embedding for table:', fqTable, errorMessage)
            throw new Error(`Failed to fetch embedding for table ${fqTable}: ${errorMessage}`)
          }

          const data = await response.json()
          const tableEmbedding = data.data[0].embedding
          const similarity = this.cosineSimilarity(queryEmbedding, tableEmbedding)
          return {
            schemaName: row.schemaName,
            tableName: row.tableName,
            similarity
          }
        })
      )

      tableSimilarities.sort((a, b) => b.similarity - a.similarity)
      return tableSimilarities.slice(0, maxRelevantTables)
    },
    stripSQLDelimiters(sql) {
      return sql.replace(/^```sql\s*/, '').replace(/```$/, '')
    },
    async genSQL() {
      const { editor } = this
      const { content } = this

      if (editor.settings.searchPath.length === 0) {
        this.$dialogs.info('Please set your Search Path')
        return
      }

      if (
        !this.copilotQuestion ||
        !this.copilotQuestion.trim()
      ) {
        this.$dialogs.info('Please enter a question to continue')
        return
      }

      const question = this.copilotQuestion.trim()
      this.copilotLoading = true

      try {
        const maxMCV = this.$store.get('settings/editor@maxMCV')

        const schemaDetailsArray = await Promise.all(
          editor.settings.searchPath.map(schema =>
            databaseObjectService.getSchemaDetails(this.editor.settings.instance, editor.settings.database, schema, maxMCV)
          )
        )

        let flattenedSchemaDetails = schemaDetailsArray.flat()
        const maxRelevantTables = this.$store.get('settings/editor@maxRelevantTables')
        const embeddingModelName = this.$store.get('settings/editor@embeddingModelName')
        if (embeddingModelName && maxRelevantTables > 0) {
          // First filter on any table selections
          const selectedTables = this.copilotSelectedTables
          if (selectedTables?.length > 0) {
            flattenedSchemaDetails = flattenedSchemaDetails.filter((row) => {
              return selectedTables.some(
                relevant =>
                  relevant.schemaName === row.schemaName && relevant.tableName === row.tableName
              )
            })
          }

          // Find unique tables from the possibly filtered list
          const uniqueSchemaTables = []
          const seenTables = new Set()
          for (const row of flattenedSchemaDetails) {
            const tableKey = `${row.schemaName}.${row.tableName}`
            if (!seenTables.has(tableKey)) {
              uniqueSchemaTables.push({
                schemaName: row.schemaName,
                tableName: row.tableName
              })
              seenTables.add(tableKey)
            }
          }

          // ...then find the relevant tables
          const relevantTables = await this.getRelevantTables(question, uniqueSchemaTables, embeddingModelName, maxRelevantTables)
          flattenedSchemaDetails = flattenedSchemaDetails.filter((row) => {
            return relevantTables.some(
              relevant =>
                relevant.schemaName === row.schemaName && relevant.tableName === row.tableName
            )
          })
        } else {
          const selectedTables = this.copilotSelectedTables
          if (selectedTables?.length > 0) {
            flattenedSchemaDetails = flattenedSchemaDetails.filter((row) => {
              return selectedTables.some(
                relevant =>
                  relevant.schemaName === row.schemaName && relevant.tableName === row.tableName
              )
            })
          } else {
            flattenedSchemaDetails = []
          }
        }

        flattenedSchemaDetails.sort((a, b) => {
          if (a.schemaName === b.schemaName) {
            return a.tableName.localeCompare(b.tableName)
          }
          return a.schemaName.localeCompare(b.schemaName)
        })

        let schemaDetails = ''
        let currentTableKey = null
        let addedHeaderForCurrentTable = false
        for (const row of flattenedSchemaDetails) {
          const tableKey = `${row.schemaName}.${row.tableName}`

          if (tableKey !== currentTableKey) {
            if (currentTableKey) {
              schemaDetails += '\n\n'
            }
            schemaDetails += `### Table: ${tableKey}\n`
            schemaDetails += '```sql\n'
            schemaDetails += `${row.tableDefinition}\n`
            schemaDetails += '```\n\n'
            currentTableKey = tableKey
            addedHeaderForCurrentTable = false
          }

          if (row.column && row.mostCommonValues.length > 0) {
            if (!addedHeaderForCurrentTable) {
              schemaDetails += `#### Most common values for table ${tableKey}\n`
              addedHeaderForCurrentTable = true
            }
            schemaDetails += `- **Column**: ${row.column}\n`
            schemaDetails += `  - **Values**: ${row.mostCommonValues.join(", ")}\n`
          }
        }

        const prompt = this.copilotPrompt
          .replace('${schema}', schemaDetails)
          .replace('${question}', question)
        const baseURL = this.$store.get('settings/editor@baseURL')
        const modelName = this.$store.get('settings/editor@modelName')
        const apiKey = this.$store.get('settings/editor@apiKey')

        const messages = [
          {
            role: "system",
            content: "You are a PostgreSQL expert who generates SQL queries from schemas and questions."
          },
          { role: "user", content: prompt }
        ]

        const response = await fetch('/copilot', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            targetUrl: `${baseURL}/v1/chat/completions`,
            apiKey,
            model: modelName,
            messages
          })
        })

        if (!response.ok) {
          const errorText = await response.text()
          let errorMessage = 'Unknown error occurred'
          try {
            const errorData = JSON.parse(errorText)
            errorMessage = errorData.details?.error?.message || errorData.message || errorMessage
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError)
            errorMessage = errorText
          }
          console.error('Failed to fetch completion:', errorMessage)
          throw new Error(`Failed to fetch completion: ${errorMessage}`)
        }

        const data = await response.json()
        const sqlGenerated = this.stripSQLDelimiters(data.choices[0]?.message?.content.trim())

        if (this.content.$refs.editor) {
          const monacoEditor = this.content.$refs.editor.editorInstance
          const oldPos = monacoEditor.getPosition()

          this.content.execute('paste', { text: sqlGenerated })
          this.copilotGeneratedPrompt = prompt

          this.$nextTick(() => {
            const newPos = monacoEditor.getPosition()
            monacoEditor.setSelection({
              startLineNumber: oldPos.lineNumber,
              startColumn: oldPos.column,
              endLineNumber: newPos.lineNumber,
              endColumn: newPos.column
            })
            monacoEditor.focus()
            monacoEditor.revealLine(oldPos.lineNumber)
          })
        }
      } catch (error) {
        this.copilotGeneratedPrompt = '### Error\n' + error.message
        this.$dialogs.warning(error.message)
      } finally {
        this.copilotLoading = false
        this.$emit("close")
      }
    }
  }
}
</script>
